/**
 * ReportStatusIcon - Shared status icon component for reports
 *
 * Used in both ReportDetails and PopupReportPage for consistent status display.
 */
import React from 'react';
import { cn } from '@ais-ui/utils';
import {
  PiCheckCircleFill,
  PiXCircleFill,
  PiProhibit,
  PiSpinner,
  PiClock,
} from 'react-icons/pi';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ais-ui/ui-core';

export type ReportStatus =
  | 'FINISHED'
  | 'FINISHED_SCHEDULED'
  | 'FAILED'
  | 'EXECUTING'
  | 'PENDING'
  | 'ABORTED'
  | 'NONE'
  | string;

interface StatusConfig {
  icon: React.ReactNode;
  label: string;
  colorClass: string;
}

const getStatusConfig = (
  status: ReportStatus,
  failureReason?: string,
): StatusConfig => {
  switch (status) {
    case 'FINISHED':
    case 'FINISHED_SCHEDULED':
      return {
        icon: <PiCheckCircleFill className="size-4" />,
        label: 'Finished',
        colorClass: 'text-success',
      };
    case 'FAILED':
      return {
        icon: <PiXCircleFill className="size-4" />,
        label: failureReason ? `Failed: ${failureReason}` : 'Failed',
        colorClass: 'text-destructive',
      };
    case 'EXECUTING':
      return {
        icon: <PiSpinner className="size-4 animate-spin" />,
        label: 'Executing',
        colorClass: 'text-blue-600',
      };
    case 'PENDING':
      return {
        icon: <PiClock className="size-4" />,
        label: 'Pending',
        colorClass: 'text-muted-foreground',
      };
    case 'ABORTED':
      return {
        icon: <PiProhibit className="size-4" />,
        label: 'Aborted',
        colorClass: 'text-orange-500',
      };
    case 'NONE':
    default:
      return {
        icon: <PiClock className="size-4" />,
        label: status || 'Unknown',
        colorClass: 'text-muted-foreground',
      };
  }
};

interface ReportStatusIconProps {
  status: ReportStatus;
  failureReason?: string;
  showLabel?: boolean;
  showTooltip?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Display a status icon with optional label and tooltip
 */
export const ReportStatusIcon: React.FC<ReportStatusIconProps> = ({
  status,
  failureReason,
  showLabel = false,
  showTooltip = true,
  className,
  size = 'md',
}) => {
  const config = getStatusConfig(status, failureReason);

  const sizeClasses = {
    sm: 'size-3',
    md: 'size-4',
    lg: 'size-5',
  };

  const iconElement = (
    <div className={cn('flex items-center gap-2', className)}>
      <span className={cn(config.colorClass, sizeClasses[size])}>
        {config.icon}
      </span>
      {showLabel && (
        <span className={cn('text-sm', config.colorClass)}>{config.label}</span>
      )}
    </div>
  );

  if (showTooltip && !showLabel) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{iconElement}</TooltipTrigger>
        <TooltipContent>{config.label}</TooltipContent>
      </Tooltip>
    );
  }

  return iconElement;
};

/**
 * Get status display text
 */
export const getStatusLabel = (status: ReportStatus): string => {
  const config = getStatusConfig(status);
  return config.label;
};

/**
 * Get status color class
 */
export const getStatusColorClass = (status: ReportStatus): string => {
  const config = getStatusConfig(status);
  return config.colorClass;
};
